import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Card, Drawer, Empty, Form, Input, message, Popconfirm,
  Select, Space, Spin, Tag, Typography,
} from "antd";
import { useState } from "react";
import {
  createRole, deleteRole, listPermissions, listRoles, updateRole,
  type RoleOut,
} from "./api";

export function RolesPage() {
  const qc = useQueryClient();
  const [msgApi, msgCtx] = message.useMessage();
  const [openCreate, setOpenCreate] = useState(false);
  const [editRole, setEditRole] = useState<RoleOut | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const rolesQ = useQuery({ queryKey: ["admin-roles"], queryFn: listRoles });
  const permsQ = useQuery({ queryKey: ["admin-perms"], queryFn: listPermissions });

  const permOptions = (permsQ.data ?? []).map(p => ({
    value: p.code, label: `${p.name} (${p.code})`,
  }));

  const reload = () => qc.invalidateQueries({ queryKey: ["admin-roles"] });

  const createM = useMutation({
    mutationFn: createRole,
    onSuccess: () => { msgApi.success("已创建"); setOpenCreate(false); createForm.resetFields(); reload(); },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const updateM = useMutation({
    mutationFn: ({ rid, body }: { rid: string; body: Parameters<typeof updateRole>[1] }) =>
      updateRole(rid, body),
    onSuccess: () => { msgApi.success("已更新"); setEditRole(null); reload(); },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const delM = useMutation({
    mutationFn: deleteRole,
    onSuccess: () => { msgApi.success("已删除"); reload(); },
    onError: (e: Error) => msgApi.error(e.message),
  });

  return (
    <div>
      {msgCtx}
      <Card
        title="角色管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />}
            onClick={() => { createForm.resetFields(); setOpenCreate(true); }}>
            新建角色
          </Button>
        }
      >
        {rolesQ.isLoading && <Spin />}
        {rolesQ.data?.length === 0 && <Empty />}
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          {(rolesQ.data ?? []).map(r => (
            <Card key={r.id} type="inner" size="small"
              title={
                <Space>
                  <Tag color="blue">{r.code}</Tag>
                  <span>{r.name}</span>
                </Space>
              }
              extra={
                <Space>
                  <Button size="small" icon={<EditOutlined />}
                    onClick={() => {
                      // RoleOut.permissions(权限 code 列表)→ 表单字段 permission_codes,回显已配权限
                      editForm.setFieldsValue({
                        name: r.name,
                        description: r.description,
                        permission_codes: r.permissions,
                      });
                      setEditRole(r);
                    }}>
                    编辑
                  </Button>
                  {r.code !== "admin" && (
                    <Popconfirm title="删除该角色?" onConfirm={() => delM.mutate(r.id)}>
                      <Button size="small" danger icon={<DeleteOutlined />}>删</Button>
                    </Popconfirm>
                  )}
                </Space>
              }
            >
              {r.description && (
                <Typography.Paragraph type="secondary" style={{ marginBottom: 6 }}>
                  {r.description}
                </Typography.Paragraph>
              )}
              <Space wrap size={4}>
                {r.permissions.length === 0
                  ? <Typography.Text type="secondary">未配权限</Typography.Text>
                  : r.permissions.map(code => <Tag key={code}>{code}</Tag>)
                }
              </Space>
            </Card>
          ))}
        </Space>
      </Card>

      <Drawer
        open={openCreate} title="新建角色" width={520}
        onClose={() => setOpenCreate(false)}
      >
        <Form form={createForm} layout="vertical" onFinish={(v) => createM.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true, pattern: /^[a-z][a-z0-9_\-]*$/ }]}>
            <Input placeholder="小写字母开头" />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="permission_codes" label="权限" initialValue={[]}>
            <Select mode="multiple" options={permOptions} loading={permsQ.isLoading} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={createM.isPending}>创建</Button>
        </Form>
      </Drawer>

      <Drawer
        open={!!editRole}
        title={editRole ? `编辑:${editRole.name}` : "编辑"}
        width={520}
        onClose={() => setEditRole(null)}
      >
        <Form form={editForm} layout="vertical"
          onFinish={(v) => editRole && updateM.mutate({ rid: editRole.id, body: v })}>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="permission_codes" label="权限">
            <Select mode="multiple" options={permOptions} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={updateM.isPending}>保存</Button>
            <Button onClick={() => setEditRole(null)}>取消</Button>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
}
